import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { asJxgPoint, type ElementCreatorContext } from '../core/board.types';
import { parseThemeColor, resolveColor } from '../core/color.utils';
import { deepMerge } from '../core/deepMerge';
import { attachReconfigure, bindLabel, createCurveLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import { resolveAnchorAt } from '../label/label.position';
import type { CurveStyle, ElementTheme } from '../theme/theme.model';
import type { CurveArgs, CurveResult, DataCurveArgs, FunctionGraphCurveArgs, ParametricCurveArgs } from './curve.model';

/**
 * Build JSXGraph curve attributes from style configuration.
 */
function buildCurveAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['curve'],
  styleOverrides?: CurveStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);
  const strokeColor = resolveColor(themeService, style.color);

  const attrs: Record<string, unknown> = {
    strokeColor,
    strokeWidth: style.strokeWidth,
    dash: style.dash,
    visible: styleOverrides?.visible ?? true,
    withLabel: false,
  };

  if (style.arrows?.first) {
    attrs['firstArrow'] =
      typeof style.arrows.first === 'object'
        ? style.arrows.first
        : { type: style.arrows.type, size: style.arrows.size };
  }
  if (style.arrows?.last) {
    attrs['lastArrow'] =
      typeof style.arrows.last === 'object' ? style.arrows.last : { type: style.arrows.type, size: style.arrows.size };
  }

  return attrs;
}

/**
 * Create a function graph curve (y = f(x)).
 * Label is configured via style.label.
 */
function createFunctionGraphCurve(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: FunctionGraphCurveArgs,
): CurveResult {
  const { board } = ctx;
  const { fn, domain, name = '', style, getExpression } = args;

  const attrs = buildCurveAttributes(themeService, theme.curve, style);

  // Create the function graph
  const curveArgs: unknown[] = [fn];
  if (domain) {
    curveArgs.push(domain[0], domain[1]);
  }

  const curve = board.create('functiongraph', curveArgs, attrs) as GeometryElement;

  // Always create label — visibility is controlled via config
  // Use theme-merged color so the label matches the curve even when no explicit color is set
  const mergedCurveColor = deepMerge(theme.curve, style)?.color;
  const labelColor = parseThemeColor(mergedCurveColor ?? 'neutral').name;
  const labelConfig = resolveLabelConfig(style?.label, { color: labelColor, precision: ctx.defaultPrecision });

  const t = resolveAnchorAt(labelConfig.anchorAt);
  // Reactive anchor: recomputes visible domain on every frame so the label tracks panning/zooming
  const getVisibleAnchorX = () => {
    const dMin = domain ? (typeof domain[0] === 'function' ? domain[0]() : domain[0]) : -Infinity;
    const dMax = domain ? (typeof domain[1] === 'function' ? domain[1]() : domain[1]) : Infinity;
    const bbox = board.getBoundingBox();
    const xMin = Math.max(dMin, bbox[0]);
    const xMax = Math.min(dMax, bbox[2]);
    return xMin + t * (xMax - xMin);
  };

  const anchorPoint = board.create('point', [() => getVisibleAnchorX(), () => fn(getVisibleAnchorX())], {
    visible: false,
  }) as GeometryElement;

  // Finite-difference slope at the anchor point
  const h = 1e-5;
  const slopeProvider = () => {
    const x = asJxgPoint(anchorPoint).X();
    return Math.atan2(fn(x + h) - fn(x - h), 2 * h);
  };

  const label = bindLabel({
    ctx,
    themeService,
    element: anchorPoint,
    config: labelConfig,
    contextProvider: createCurveLabelContext(name || '', getExpression),
    slopeProvider,
  });
  attachReconfigure(curve, label);

  return { curve, label, helpers: [anchorPoint] };
}

/**
 * Create a parametric curve.
 * Label is configured via style.label.
 */
function createParametricCurve(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: ParametricCurveArgs,
): CurveResult {
  const { board } = ctx;
  const { x, y, range, name = '', style } = args;

  const attrs = buildCurveAttributes(themeService, theme.curve, style);

  // Create the parametric curve
  const curve = board.create('curve', [x, y, range[0], range[1]], attrs) as GeometryElement;

  // Always create label — visibility is controlled via config
  const mergedCurveColor = deepMerge(theme.curve, style)?.color;
  const labelColor = parseThemeColor(mergedCurveColor ?? 'neutral').name;
  const labelConfig = resolveLabelConfig(style?.label, { color: labelColor, precision: ctx.defaultPrecision });

  const tFrac = resolveAnchorAt(labelConfig.anchorAt);
  const anchorT = range[0] + tFrac * (range[1] - range[0]);
  const labelPoint = board.create('point', [() => x(anchorT), () => y(anchorT)], {
    visible: false,
  }) as GeometryElement;

  // Finite-difference slope along the parametric curve
  const h = 1e-5;
  const slopeProvider = () => Math.atan2(y(anchorT + h) - y(anchorT - h), x(anchorT + h) - x(anchorT - h));

  const label = bindLabel({
    ctx,
    themeService,
    element: labelPoint,
    config: labelConfig,
    contextProvider: createCurveLabelContext(name || ''),
    slopeProvider,
  });
  attachReconfigure(curve, label);

  return { curve, label, helpers: [labelPoint] };
}

/**
 * Create a data curve from x/y arrays.
 * Label is configured via style.label.
 */
function createDataCurve(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: DataCurveArgs,
): CurveResult {
  const { board } = ctx;
  const { xData, yData, name = '', style } = args;

  const attrs = buildCurveAttributes(themeService, theme.curve, style);

  // Create the data curve
  const curve = board.create('curve', [xData, yData], attrs) as GeometryElement;

  // Always create label — visibility is controlled via config
  const mergedDataCurveColor = deepMerge(theme.curve, style)?.color;
  const labelColor = parseThemeColor(mergedDataCurveColor ?? 'neutral').name;
  const labelConfig = resolveLabelConfig(style?.label, { color: labelColor, precision: ctx.defaultPrecision });

  const anchorX =
    xData.length > 0
      ? xData[Math.min(Math.round(resolveAnchorAt(labelConfig.anchorAt) * (xData.length - 1)), xData.length - 1)]
      : 0;
  const anchorY =
    xData.length > 0
      ? yData[Math.min(Math.round(resolveAnchorAt(labelConfig.anchorAt) * (xData.length - 1)), xData.length - 1)]
      : 0;
  const labelPoint = board.create('point', [anchorX, anchorY], { visible: false }) as GeometryElement;

  // Slope from neighboring data points
  const idx =
    xData.length > 0
      ? Math.min(Math.round(resolveAnchorAt(labelConfig.anchorAt) * (xData.length - 1)), xData.length - 1)
      : 0;
  const slopeProvider = () => {
    if (xData.length === 0) {
      return 0;
    }
    const prev = Math.max(0, idx - 1);
    const next = Math.min(xData.length - 1, idx + 1);
    return Math.atan2(yData[next] - yData[prev], xData[next] - xData[prev]);
  };

  const label = bindLabel({
    ctx,
    themeService,
    element: labelPoint,
    config: labelConfig,
    contextProvider: createCurveLabelContext(name || ''),
    slopeProvider,
  });
  attachReconfigure(curve, label);

  return { curve, label, helpers: [labelPoint] };
}

/**
 * Create a curve element with optional label.
 *
 * Supports three curve types:
 * - functiongraph: y = f(x)
 * - parametric: x(t), y(t) over range [t0, t1]
 * - data: arrays of x and y values
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Curve creation arguments
 * @returns Curve result with element and optional label
 */
export function createCurve(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: CurveArgs,
): CurveResult {
  switch (args.type) {
    case 'functiongraph':
      return createFunctionGraphCurve(ctx, themeService, theme, args);
    case 'parametric':
      return createParametricCurve(ctx, themeService, theme, args);
    case 'data':
      return createDataCurve(ctx, themeService, theme, args);
  }
}
